import React, { FC, useState } from "react";
import { CSVErrorProps } from "./EditObjectPools";
import { ObjectPool } from "../../../types/Objects";
import { Box, Button, FileUpload, Flex, InlineNotification, Modal, Text, useToast, useTranslation } from "@familyzone/component-library";
import { youtubeBulkImport, YoutubeBulkImportRequest } from "../../../utils/api/Youtube";

interface ImportCSVModalYoutubeProps {
  open: boolean;
  onClose: () => void;
  objectPool: ObjectPool;
}

const ImportCSVModalYoutube: FC<ImportCSVModalYoutubeProps> = ({ open, onClose, objectPool }) => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const [loaded, setLoaded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [csvErrors, setCsvErrors] = useState<CSVErrorProps[]>([]);

  // Payload state to hold valid entries before user confirmation
  const [request, setRequest] = useState<YoutubeBulkImportRequest>();

  const handleUpload = (file: File, done: (success: boolean) => void) => {
    setError("");
    setCsvErrors([]);
    setLoaded(false);

    const reader = new FileReader();

    const newVideos: string[] = [];
    const newChannels: string[] = [];
    const newCategories: string[] = [];
    const lineErrors: CSVErrorProps[] = [];

    reader.readAsText(file);

    reader.onload = () => {
      const csv = reader.result?.toString();
      const lines = csv?.split("\n");
      if (!lines) {
        setLoaded(true);
        return;
      }
      if (lines.length > 10000) {
        setError("CSV file must not be more than 10000 lines");
        setLoaded(true);
        return;
      }

      lines?.forEach((line, index) => {
        line = line.trim();
        const values = line.split(",");

        // Skip empty lines
        if (values.length === 1 && values[0] === "") {
          return;
        }

        // Validate number of columns
        if (values.length !== 2) {
          lineErrors.push({
            line: index + 1,
            error: "CSV Format does not match. Please follow the format in the example template",
            entry: values[0],
          });
          return;
        }

        // Skip header line
        if (values[0] === "Type" && values[1] === "Entry") {
          return;
        }

        // Validate type
        const type = values[0].toLowerCase();
        if (type !== "video" && type !== "channel" && type !== "category") {
          lineErrors.push({
            line: index + 1,
            error: `Invalid type '${values[0]}'. Must be 'video', 'channel', or 'category'`,
            entry: values[1],
          });
          return;
        }

        // Validate entry
        if (values[1] === "") {
          return;
        }

        switch (type) {
          case "video":
            if (newVideos.find((u) => u === values[1])) {
              lineErrors.push({
                line: index + 1,
                error: "Entry already exists",
                entry: values[1],
              });
              return;
            }
            newVideos.push(values[1]);
            break;

          case "category":
            if (newCategories.find((u) => u === values[1])) {
              lineErrors.push({
                line: index + 1,
                error: "Entry already exists",
                entry: values[1],
              });
              return;
            }
            newCategories.push(values[1]);
            break;

          case "channel":
            if (newChannels.find((u) => u === values[1])) {
              lineErrors.push({
                line: index + 1,
                error: "Entry already exists",
                entry: values[1],
              });
              return;
            }
            newChannels.push(values[1]);
            break;

          default:
            break;
        }
      });

      if (newVideos.length === 0 && newChannels.length === 0 && newCategories.length === 0) {
        setError("No valid entries found in CSV");
        setLoaded(true);
        done(false);
        return;
      }

      const request: YoutubeBulkImportRequest = {
        objectPool: objectPool,
        videos: newVideos,
        channels: newChannels,
        categories: newCategories,
      };

      if (lineErrors.length > 0) {
        // Even if there are line errors, we hold the payload with valid entries before user confirmation
        setRequest(request);
        setCsvErrors(lineErrors);
        setLoaded(true);
        done(false);
        return;
      }

      // If no errors, proceed to submit immediately. Pass the payload directly to handler to avoid race condition.
      handleUploadConfirmed(request);
      done(true);
    };
  };

  const handleUploadConfirmedAsync = async (payload: YoutubeBulkImportRequest) => {
    const ok = await youtubeBulkImport(payload);

    if (ok) {
      successToast({
        title: "CSV import is processing in the background",
        description: `The YouTube list is being processed with ${payload.videos.length} videos, ${payload.channels.length} channels, and ${payload.categories.length} categories. Please allow a few minutes for this to complete, then refresh the page to view the updates.`,
        isClosable: true,
      });
      // Close the modal on successful submission
      handleClose();
    } else {
      errorToast({
        title: "There was an error processing your CSV import.",
        description: "Please refresh the page or try again later.",
        isClosable: true,
      });
    }

    setLoaded(true);
    // Clear the saved request after submission
    setRequest(undefined);
  };

  /**
   * Handles the confirmed upload after user reviews errors.
   * @param payload Optional payload to use for submission. If not provided, uses the saved request state.
   */
  const handleUploadConfirmed = (payload?: YoutubeBulkImportRequest) => {
    if (!payload) {
      // This should not happen, just in case
      if (!request) return;
      payload = request;
    }

    void handleUploadConfirmedAsync(payload);
  };

  const handleDownloadErrors = () => {
    const headers = ["Line", "Error Message", "Entry"];
    const csv = csvErrors.map((error) => `${error.line},${error.error},${error.entry}`).join("\n");
    const csvData = [headers, csv].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvData], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = "errors.csv";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const handleClose = () => {
    setCsvErrors([]);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      headerText="Import CSV"
      contentProps={{ style: { maxHeight: "700px" } }}
      size={csvErrors.length > 0 ? "md" : "sm"}
    >
      <Box>
        <Flex direction="column" pt="sp16" pb="sp16" gap="sp8">
          {error && csvErrors.length === 0 && (
            <Flex>
              <InlineNotification
                notificationTitle="CSV Import Error"
                onCloseButtonClick={handleClose}
                status="error"
                notificationDescription={
                  <Flex direction="column">
                    <Text>{error}</Text>
                  </Flex>
                }
              />
            </Flex>
          )}
          <Flex mb="sp24">
            <InlineNotification
              status="warning"
              notificationDescription={
                <>
                  <Text>
                    <b>Warning</b>: Importing a CSV will overwrite all existing entries. This cannot be undone.
                  </Text>
                  <Text>Invalid or malformed rows in the CSV will be skipped, but all valid entries will be imported.</Text>
                </>
              }
            />
          </Flex>
          <Flex justifyContent={"center"}>
            <Box width="100%" tabIndex={-1}>
              <FileUpload
                secondaryText="Only CSV format is supported"
                primaryText="Select a file or drag and drop here"
                accept="text/csv"
                multiple={false}
                onUpload={handleUpload}
                data-testid="Import CSV"
                disabled={!loaded}
                aria-label="Import CSV File"
                dragAndDrop={true}
                autoUpload={true}
                clearOnUpload={true}
              />
            </Box>
          </Flex>
          {csvErrors.length > 0 && (
            <Box>
              <Flex direction="column">
                <Flex pt="sp16">
                  <InlineNotification
                    status="error"
                    notificationDescription={t(`There are problem(s) with your CSV import, ${csvErrors.length} issues identified:`)}
                  />
                </Flex>
                <Box
                  sx={
                    csvErrors.length > 3
                      ? { overflowY: "scroll", maxHeight: "10vh", border: "1px solid #eee", borderRadius: "4px" }
                      : { maxHeight: "10vh" }
                  }
                >
                  {csvErrors.map((error) => (
                    <Flex p="6" key={error.line}>
                      <Text>{t(`Line ${error.line}: ${error.entry} - ${error.error}`)}</Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
              <Flex direction="row" pt="sp16" gap="sp8">
                <Flex>
                  <Button variant="primary" onClick={() => handleUploadConfirmed()}>
                    {t("Import")}
                  </Button>
                </Flex>
                <Flex grow={1} />
                <Flex>
                  <Button variant="danger" onClick={handleDownloadErrors}>
                    {t("Export Errors")}
                  </Button>
                </Flex>
                <Flex>
                  <Button onClick={onClose} variant="outline" aria-label="close">
                    {t("Close")}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          )}
        </Flex>
      </Box>
    </Modal>
  );
};
export default ImportCSVModalYoutube;
